import { getEnvAndRegion } from "../components/inputComponents/FileUpload/fileUploadHelpers";
import awsconfig from '../aws-exports';
import { ConfigType, PluginRoute } from "../types/configuration";

const { env } = getEnvAndRegion(awsconfig.aws_user_files_s3_bucket);
const systemId = "receipt-scanner-promoplus";

export const isFormUnchanged = (formikValues: any, initialFormikValues: any) => {
    return JSON.stringify(formikValues) === JSON.stringify(initialFormikValues);
};

// * RS Utils

export const isProdStage = () => {
    return ['prod', 'production', 'production-it'].includes(env)
};

const fetchUrl = () =>
    isProdStage()
    ? 'https://prod-emea-api.rs.coca-cola.com'
    : 'https://stage-api.rs.coca-cola.com';

const getSystemId = () => isProdStage() ? systemId : `${systemId}-stage`;

const hasParams = (param: object, entries?: object) => !!(param && Object.entries(entries ? entries: param)?.length);

export const isRsPresentInConfig = (config: ConfigType, promoFlow: string) => {
    const hasPluginParams = hasParams(config?.pluginParams);
    const hasUploadImage = hasParams(config?.flow?.uploadImage, config.flow.uploadImage?.pluginRoutes?.getSignedUrls);
    const hasFlowPluginRoutes = hasParams(config?.flow?.[promoFlow]?.pluginRoutes,
    config.flow?.[promoFlow]?.pluginRoutes?.processReceipts);

    return hasPluginParams || hasUploadImage || hasFlowPluginRoutes;
}

export const getSignedUrlsPluginRoute = (): Record<'getSignedUrls', PluginRoute<'getSignedUrls'>> => {
    return {
        getSignedUrls: {
            pluginId: "RS",
            method: "POST",
            executionOrder: "onBefore",
            middlewareToUse: "callExternalService",
            defaultRoute: true,
            fetchUrl: `${fetchUrl()}/api/receipt-scanner/v1/receipt/new`,
            requiredParams: [
                "fileNames"
            ],
            auth: {
                "type": "AWSv4"
            },
            externalReqParams: {
                "fileNames": {
                    "valueReference": "body.fileNames"
                }
            }
        }
    };
}

export const processReceiptsPluginRoute = (
    { timeout }: { timeout: number }, { market }: { market?: string } = {}
): Record<'processReceipts', PluginRoute<'processReceipts'>> => {
    const normalizedMarket = market?.trim() ?? '';

    return {
        processReceipts: {
            pluginId: "RS",
            method: "POST",
            middlewareToUse: "callExternalService",
            executionOrder: "onBefore",
            externalReqParams: {
                timeoutInMillis: timeout,
                systemId: getSystemId(),
                systemMetadata: {
                    pluginId: "RS"
                },
                market: normalizedMarket,
                scannerConfigInstanceId: '',
                externalId: {
                    valueReference: "body.participationId"
                },
                promotionId: {
                    valueReference: "queryStringParameters.configurationId"
                },
                userId: {
                    valueReference: "requestContext.authorizer.hashed_kocid"
                },
                signedUrls: {
                    valueReference: "body.signedUrls"
                },
            },
            fetchUrl: `${fetchUrl()}/api/receipt-scanner/v1/receipt`,
            requiredParams: [
                "signedUrls"
            ],
            auth: {
                type: "AWSv4"
            }
        },
    }
}

export const coreLogicWithPartId = (): Record<'coreLogicWithPartId', PluginRoute<'coreLogicWithPartId'>> => {
    return {
        coreLogicWithPartId: {
            pluginId: "RS",
            middlewareToUse: "createOrAppendParticipation",
            executionOrder: "onBefore",
            requiredParams: [
                "participationId"
            ]
        }
    }
}

export const generateRsConfigParams = (marketInput?: string) => {
    const pluginRoutes = {
        ...processReceiptsPluginRoute({ timeout: 8000 }, { market: marketInput }),
        ...coreLogicWithPartId(),
    }
    if (!isProdStage()) {
        pluginRoutes.processReceipts.externalReqParams.processingOptions = [
            "skipValidateSimpleDuplication",
            "skipValidateComplexDuplication"
        ]
    }
    const uploadImage = {
        pluginRoutes: { ...getSignedUrlsPluginRoute() }
    }
    const pluginParams = {
        url: {},
        emailTemplateId: '',
        rejectedEmailTemplateId: '',
        rsAllocationMap: {}
    }

    return { pluginRoutes, uploadImage, pluginParams }
}
